import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';

const ServiceResponseViewer = ({ results, isLoading }) => {
  const [expandedSections, setExpandedSections] = useState({
    request: false,
    response: true,
    metadata: false
  });

  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadResponse = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (success) => {
    return success ? 'success' : 'error';
  };

  const getStatusIcon = (success) => {
    return success ? <CheckCircleIcon /> : <ErrorIcon />;
  };

  const renderLoadingState = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      py: 4,
      color: 'text.secondary'
    }}>
      <CircularProgress size={40} sx={{ mb: 2 }} />
      <Typography variant="body2">
        Testing service...
      </Typography>
    </Box>
  );

  const renderEmptyState = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      py: 4,
      color: 'text.secondary'
    }}>
      <InfoIcon sx={{ fontSize: 40, mb: 2 }} />
      <Typography variant="body2">
        No test results yet
      </Typography>
      <Typography variant="caption">
        Select a service and run a test to see results
      </Typography>
    </Box>
  );

  const renderRequestSection = () => (
    <Accordion 
      expanded={expandedSections.request} 
      onChange={handleAccordionChange('request')}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Request Details</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Service:</Typography>
            <Chip label={results.service} size="small" />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Timestamp:</Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon fontSize="small" />
              {formatTimestamp(results.timestamp)}
            </Typography>
          </Stack>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Input Parameters:</Typography>
            <Tooltip title="Copy to clipboard">
              <IconButton 
                size="small" 
                onClick={() => copyToClipboard(JSON.stringify(results.inputs, null, 2))}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <pre style={{ 
              fontSize: '0.8rem', 
              margin: 0, 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {JSON.stringify(results.inputs, null, 2)}
            </pre>
          </Paper>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderResponseSection = () => (
    <Accordion 
      expanded={expandedSections.response} 
      onChange={handleAccordionChange('response')}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Response</Typography>
          <Chip 
            icon={getStatusIcon(results.success)}
            label={results.success ? 'Success' : 'Error'}
            color={getStatusColor(results.success)}
            size="small"
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">
              {results.success ? 'Response Data:' : 'Error Details:'}
            </Typography>
            <Tooltip title="Copy to clipboard">
              <IconButton 
                size="small" 
                onClick={() => copyToClipboard(JSON.stringify(results.success ? results.response : results.error, null, 2))}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download as JSON">
              <IconButton 
                size="small" 
                onClick={() => downloadResponse(
                  results.success ? results.response : results.error,
                  `${results.service}_${results.success ? 'response' : 'error'}_${Date.now()}.json`
                )}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Paper sx={{ 
            p: 2, 
            bgcolor: results.success ? 'success.light' : 'error.light',
            color: results.success ? 'success.contrastText' : 'error.contrastText'
          }}>
            <pre style={{ 
              fontSize: '0.8rem', 
              margin: 0, 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {JSON.stringify(results.success ? results.response : results.error, null, 2)}
            </pre>
          </Paper>
        </Box>
        
        {results.success && results.response && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Response Summary:
            </Typography>
            
            {/* Special handling for geocoding responses */}
            {results.service === 'GEOCODING' && results.response.results && results.response.results.length > 0 ? (
              <Box>
                {/* Summary stats */}
                {results.response.summary && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      📊 <strong>Summary:</strong> {results.response.summary.success_count || 0} of {results.response.summary.total_processed || 0} locations successfully geocoded
                    </Typography>
                  </Alert>
                )}
                
                {/* Location results */}
                {results.response.results.map((location, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: location.status === 'SUCCESS' ? 'success.light' : 'error.light' }}>
                    {location.status === 'SUCCESS' ? (
                      <List dense>
                        <ListItem>
                          <ListItemIcon><LocationOnIcon fontSize="small" /></ListItemIcon>
                          <ListItemText 
                            primary="📍 Coordinates"
                            secondary={`${location.latitude}, ${location.longitude}`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                          <ListItemText 
                            primary="🏛️ County"
                            secondary={location.county || 'Not available'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                          <ListItemText 
                            primary="📝 Formatted Address"
                            secondary={location.formatted_address || 'Not available'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                          <ListItemText 
                            primary="🔗 Place ID"
                            secondary={location.place_id || 'Not available'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                          <ListItemText 
                            primary="📥 Input Address"
                            secondary={location.input_address || 'Not available'}
                          />
                        </ListItem>
                      </List>
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="error">
                          ❌ Geocoding Failed
                        </Typography>
                        <Typography variant="body2">
                          <strong>Input:</strong> {location.input_address}
                        </Typography>
                        <Typography variant="body2" color="error">
                          <strong>Error:</strong> {location.error || 'Unknown error'}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            ) : (
              /* Default response display for other services */
              <List dense>
                {Object.entries(results.response).map(([key, value]) => (
                  <ListItem key={key}>
                    <ListItemIcon>
                      <InfoIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={key}
                      secondary={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );

  const renderMetadataSection = () => (
    <Accordion 
      expanded={expandedSections.metadata} 
      onChange={handleAccordionChange('metadata')}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Metadata</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <InfoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Service Tested"
              secondary={results.service}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <AccessTimeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Test Time"
              secondary={formatTimestamp(results.timestamp)}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {getStatusIcon(results.success)}
            </ListItemIcon>
            <ListItemText 
              primary="Result Status"
              secondary={results.success ? 'Success' : 'Failed'}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <InfoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Response Type"
              secondary={results.success ? typeof results.response : 'Error'}
            />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>
  );

  if (isLoading) {
    return renderLoadingState();
  }

  if (!results) {
    return renderEmptyState();
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Alert severity={results.success ? 'success' : 'error'}>
          <Typography variant="subtitle2">
            {results.success ? 'Test Completed Successfully' : 'Test Failed'}
          </Typography>
          <Typography variant="body2">
            {results.success ? 
              'The service responded successfully. Check the response data below.' : 
              'There was an error during the test. Check the error details below.'}
          </Typography>
        </Alert>
      </Box>

      {renderRequestSection()}
      {renderResponseSection()}
      {renderMetadataSection()}
    </Box>
  );
};

export default ServiceResponseViewer; 